import { getYesNoOrUndefinedSubClassAnswer } from './createDetailedMedicationData'

export enum SubstanceFrequency {
  HOURLY = 'hourly',
  DAILY = 'daily',
  MONTHLY = 'monthly',
  YEARLY = 'yearly',
}

export const SubstanceFrequencyToHours: Record<SubstanceFrequency, number> = {
  [SubstanceFrequency.HOURLY]: 1,
  [SubstanceFrequency.DAILY]: 24,
  [SubstanceFrequency.MONTHLY]: 720,
  [SubstanceFrequency.YEARLY]: 8760,
}

export type DetailedSubstanceData = {
  frequencyOfUse?: SubstanceFrequency
  name?: string
  willingToStop?: string
}[]

/**
 * Create object containing substance name and details about if a patient iswilling to stop and frequency of use
 *
 * @param areCurrentSubstancesWillingToStop Patient answer to question about if substances are willing to stop
 * @param currentSubstanceFrequency Patient answer to question about frequency of use
 * @param substanceNamesPatientSelected Array of substance names selected by patient
 *
 * @return array of substances with details provided by patient
 */
export function createDetailedSubstanceData({
  areCurrentSubstancesWillingToStop,
  currentSubstanceFrequency,
  substanceNamesPatientSelected,
}: {
  areCurrentSubstancesWillingToStop?: string
  currentSubstanceFrequency?: string
  substanceNamesPatientSelected: string[]
}): DetailedSubstanceData {
  const substances = []
  for (const substance of substanceNamesPatientSelected) {
    // In the eligibility concept table, the name is stored as string - so we need to convert the yes/no/unsure to a string boolean. The column is polymorphic.
    const willingToStop = getYesNoOrUndefinedSubClassAnswer(
      areCurrentSubstancesWillingToStop,
    )
    const frequencyOfUse = getFrequencyOrUndefinedSubClassAnswer(
      currentSubstanceFrequency,
    )

    substances.push({
      name: substance,
      ...(willingToStop && { willingToStop }),
      ...(frequencyOfUse && { frequencyOfUse }),
    })
  }
  return substances
}

/**
 * Check if the value is a valid SubstanceFrequency
 * In all other cases, return undefined
 *
 * @param value String to check if it is a valid SubstanceFrequency
 *
 * @return SubstanceFrequency or undefined
 */
export function getFrequencyOrUndefinedSubClassAnswer(value?: string) {
  if (Object.values(SubstanceFrequency).includes(value as SubstanceFrequency)) {
    return value as SubstanceFrequency
  }
  return
}
